import { Injectable, OnDestroy } from '@angular/core'
import { AuthService } from '@app/modules/auth/auth.service'
import { Observable, of, Subject } from 'rxjs'
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { ApiService } from '@shared/services/api.service'
import { negativeBoolean } from '@core/helpers/negative-boolean'
import { ActivatedRoute, Router } from '@angular/router'
import { Sort } from '@angular/material/sort'
import isPlainObject from 'lodash-es/isPlainObject'
import difference from 'lodash-es/difference'

import { entriesToObject } from '@core/helpers/entries-to-object'
import { FilterValues } from '@shared/models/filter-values.model'
import { ScheduledParamsParse } from '@shared/models/scheduled-report.model'

interface AppState {
  [route: string]: StateItem
}

interface StateItem {
  hiddenColumns: string[]
  columns?: string[]
  sort: Sort
  form: object
}

@Injectable({ providedIn: 'root' })
export class StateService implements OnDestroy {
  private storage: Storage = localStorage

  private readonly saveState$ = new Subject<AppState>()
  private readonly onDestroy$ = new Subject<void>()

  constructor(
    private authService: AuthService,
    private router: Router,
    private readonly route: ActivatedRoute,
    private apiService: ApiService,
  ) {
    this.initImpersonationSubscription()
    this.initSaveSubscription()
  }

  initImpersonationSubscription() {
    const isImpersonated$ = this.authService.isImpersonated$.pipe(takeUntil(this.onDestroy$))

    isImpersonated$.pipe(filter(Boolean)).subscribe(() => (this.storage = sessionStorage))
    isImpersonated$.pipe(filter(negativeBoolean)).subscribe(() => (this.storage = localStorage))
  }

  initSaveSubscription() {
    this.saveState$
      .pipe(
        takeUntil(this.onDestroy$),
        switchMap((state) => this.saveState(state)),
      )
      .subscribe()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadState(): Observable<any> {
    if (environment.fakeApis) {
      return of(null)
    }
    return this.apiService.get('/storage/stateTables').pipe(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      map((response) => response['data']),
      map((state: { id: number; key: string; value: string }) => {
        if (state.key === 'stateTables') {
          const objectState = JSON.parse(state.value) as AppState
          Object.keys(objectState).forEach((table) => {
            objectState[table].form = {}
          })
          if (state.value !== JSON.stringify(objectState)) {
            state.value = JSON.stringify(objectState)
            this.saveState(objectState).subscribe()
          }
        }
        return state
      }),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
      tap((state: any) => this.storage.setItem('stateTables', state.value)),
      catchError(() => of(null)),
    )
  }

  // NOTE: POST used as a silent background UI-state persist (table columns / sort / filter form),
  // fired on every column toggle, sort change, filter edit and on page load — not a user-initiated
  // save. Do NOT opt in to FormFeedbackInterceptor (withFeedback): would false-toast constantly.
  private saveState(_state: AppState) {
    if (environment.fakeApis) {
      return of(null)
    }
    return this.apiService.post('/storage/', { key: 'stateTables', value: JSON.stringify(_state) })
  }

  saveTableHiddenColumns(columns: string[]) {
    const state = this.getStateItem()
    state.hiddenColumns = columns
    this.saveStateItem(state)
  }

  getTableHiddenColumns() {
    return this.getStateItem().hiddenColumns
  }

  saveTableColumns(columns: string[]): void {
    const state = this.getStateItem()
    state.columns = columns
    this.saveStateItem(state)
  }

  getTableColumns(): string[] {
    return this.getStateItem().columns
  }

  saveTableSort(sort: Sort) {
    const state = this.getStateItem()
    state.sort = sort
    this.saveStateItem(state)
  }

  getTableSort() {
    return this.getStateItem().sort
  }

  saveForm(value: FilterValues): void {
    if (!isPlainObject(value)) {
      return
    }

    const state = this.getStateItem()
    state.form = this.parsingFilterForm(value)
    this.saveStateItem(state)
  }

  getForm() {
    return this.getStateItem().form
  }

  saveFullState({ filterValues, fields, sortValue }: ScheduledParamsParse, routerPath?: string): void {
    const appState = this.getAppState()
    const route = routerPath || this.getRouterPath()

    appState[route] = {
      form: this.parsingFilterForm(filterValues),
      sort: sortValue,
      hiddenColumns: difference(appState[route].columns, fields),
      columns: appState[route].columns,
    }

    this.updateAppState(appState)
  }

  private updateAppState(appState: AppState): void {
    this.storage.setItem('stateTables', JSON.stringify(appState))
    this.saveState$.next(appState)
  }

  private parsingFilterForm(filterValues: FilterValues): FilterValues {
    if (!isPlainObject(filterValues)) {
      return {}
    }

    const notEmpty = ([, value]) => value !== undefined && value !== null && value !== ''
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.entries(filterValues).filter(notEmpty).reduce(entriesToObject, {})
  }

  private getAppState(): AppState {
    const state = this.storage.getItem('stateTables')
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(state) || {}
    } catch (e) {
      return {}
    }
  }

  private getDefaultHiddenColumns(routePath: string, queryParams: Record<string, string>): string[] {
    const appState = this.getAppState()

    const pathInState = Object.keys(appState).includes(routePath)
    const hiddenColumnsAreNotInState = (defaultHiddenColumns: string[]) =>
      pathInState && difference(appState[routePath].columns, defaultHiddenColumns).length === defaultHiddenColumns.length

    const hideColumns = (defaultHiddenColumns: string[]) => !pathInState || hiddenColumnsAreNotInState(defaultHiddenColumns)

    let hiddenColumns: string[]
    let defaultHiddenColumns: string[]

    switch (routePath) {
      case 'client/reports/routes_details/stops':
      case 'client/reports/routes_details/stops_by_vehicle':
        defaultHiddenColumns = ['areaGroup']
        hiddenColumns = hideColumns(defaultHiddenColumns) ? defaultHiddenColumns : []
        break
      case 'client/reports/routes_details/routes':
      case 'client/reports/routes_details/routes_by_vehicle':
      case 'client/reports/business-private/fbt':
        defaultHiddenColumns = ['areaGroupStart', 'areaGroupFinish']
        hiddenColumns = hideColumns(defaultHiddenColumns) ? defaultHiddenColumns : []
        break
      case 'client/alerts/events-log-details/events-log/:id': {
        const isExceedSpeedEvent = queryParams.eventId === '105'
        defaultHiddenColumns = isExceedSpeedEvent ? ['importance'] : []
        hiddenColumns = defaultHiddenColumns
        break
      }
      default:
        hiddenColumns = []
    }

    return hiddenColumns
  }

  private getStateItem(): StateItem {
    const appState = this.getAppState()
    const routerPath = this.getRouterPath()
    const queryParams = this.route.snapshot.queryParams
    const stateItem = appState[routerPath] || {}

    const blankStateItem: StateItem = {
      hiddenColumns: [],
      columns: [],
      sort: { active: undefined, direction: 'asc' },
      form: {},
    }

    const defaultHiddenColumns = this.getDefaultHiddenColumns(routerPath, queryParams)
    const result = Object.assign(blankStateItem, stateItem)
    result.hiddenColumns.push(...defaultHiddenColumns)

    return result
  }

  private saveStateItem(item: StateItem) {
    const appState = this.getAppState()
    appState[this.getRouterPath()] = item
    this.updateAppState(appState)
  }

  private getRouterPath() {
    let segment = this.router.routerState.root

    const path = []

    while (segment) {
      segment.routeConfig && path.push(segment.routeConfig.path)
      segment = segment.children[0]
    }

    return path.filter(Boolean).join('/')
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
